<template>
  <div class="vehicles-container">
    <div class="page-header">
      <h2>车辆管理</h2>
      <el-button type="primary" @click="showAddDialog">新增车辆</el-button>
    </div>

    <!-- 搜索区域 -->
    <div class="search-section">
      <el-form :inline="true" :model="searchForm" class="search-form">
        <el-form-item label="车辆ID">
          <el-input v-model="searchForm.vehicleId" placeholder="请输入车辆ID" clearable></el-input>
        </el-form-item>
        <el-form-item label="品牌">
          <el-input v-model="searchForm.brand" placeholder="请输入品牌" clearable></el-input>
        </el-form-item>
        <el-form-item label="车型">
          <el-input v-model="searchForm.model" placeholder="请输入车型" clearable></el-input>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch">搜索</el-button>
          <el-button @click="resetSearch">重置</el-button>
        </el-form-item>
      </el-form>
    </div>

    <!-- 车辆列表 -->
    <div class="table-section">
      <el-table :data="vehicleList" v-loading="loading" border stripe>
        <el-table-column prop="vehicleId" label="车辆ID" width="100"></el-table-column>
        <el-table-column label="品牌" width="120">
          <template slot-scope="scope">
            <div class="brand-cell">
              <el-image
                v-if="scope.row.brandLogo"
                :src="scope.row.brandLogo"
                fit="contain"
                style="width: 30px; height: 20px; margin-right: 8px;"
              >
                <div slot="error" class="image-slot-small">
                  <i class="el-icon-picture-outline"></i>
                </div>
              </el-image>
              <span>{{ scope.row.brand }}</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="model" label="车型" width="120"></el-table-column>
        <el-table-column prop="modelNumber" label="车辆型号" width="120"></el-table-column>
        <el-table-column prop="vin" label="车架号" width="140"></el-table-column>
        <el-table-column prop="inspectionDate" label="检测日期" width="100"></el-table-column>
        <el-table-column prop="mileage" label="里程数" width="80">
          <template slot-scope="scope">
            {{ scope.row.mileage }}公里
          </template>
        </el-table-column>
        <el-table-column prop="grade" label="等级" width="60"></el-table-column>
        <el-table-column label="能源类型" width="80">
          <template slot-scope="scope">
            {{ scope.row.energyType === 2 ? '纯电' : '油车' }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="240" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" type="primary" @click="showEditDialog(scope.row)">编辑</el-button>
            <el-button size="mini" type="success" @click="showDetailDialog(scope.row)">详情</el-button>
            <el-button size="mini" type="danger" @click="handleDelete(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination-section">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="pagination.current"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="pagination.size"
          layout="total, sizes, prev, pager, next, jumper"
          :total="pagination.total">
        </el-pagination>
      </div>
    </div>

    <!-- 新增/编辑车辆对话框 -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="1000px" @close="resetForm">
      <el-form :model="vehicleForm" :rules="vehicleRules" ref="vehicleForm" label-width="120px">
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="车辆ID" prop="vehicleId">
              <el-input v-model="vehicleForm.vehicleId" placeholder="请输入车辆ID" :disabled="isEdit"></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="品牌" prop="brand">
              <el-select 
                v-model="vehicleForm.brand" 
                placeholder="请选择品牌" 
                @change="handleBrandChange"
                clearable
                style="width: 100%;"
              >
                <el-option
                  v-for="brand in brandOptions"
                  :key="brand.id"
                  :label="brand.label"
                  :value="brand.value"
                >
                  <div class="brand-option">
                    <img v-if="brand.logo" :src="brand.logo" class="brand-logo-small" />
                    <span>{{ brand.label }}</span>
                  </div>
                </el-option>
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="车型" prop="model">
              <el-input v-model="vehicleForm.model" placeholder="请输入车型"></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="车架号" prop="vin">
              <el-input v-model="vehicleForm.vin" placeholder="请输入车架号"></el-input>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="检测日期" prop="inspectionDate">
              <el-date-picker v-model="vehicleForm.inspectionDate" type="date" placeholder="选择检测日期" style="width: 100%"></el-date-picker>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="里程数" prop="mileage">
              <el-input-number v-model="vehicleForm.mileage" :min="0" placeholder="请输入里程数" style="width: 100%"></el-input-number>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="能源类型" prop="energyType">
              <el-select v-model="vehicleForm.energyType" placeholder="请选择能源类型" style="width: 100%">
                <el-option label="油车" :value="1"></el-option>
                <el-option label="纯电" :value="2"></el-option>
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="排量">
              <el-input v-model="vehicleForm.displacement" placeholder="请输入排量"></el-input>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="车辆类型" prop="vehicleType">
              <el-select v-model="vehicleForm.vehicleType" placeholder="请选择车辆类型" style="width: 100%">
                <el-option label="三厢轿车" value="三厢轿车"></el-option>
                <el-option label="两厢轿车" value="两厢轿车"></el-option>
                <el-option label="SUV" value="SUV"></el-option>
                <el-option label="MPV" value="MPV"></el-option>
                <el-option label="跑车" value="跑车"></el-option>
                <el-option label="皮卡" value="皮卡"></el-option>
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="型号" prop="modelNumber">
              <el-input v-model="vehicleForm.modelNumber" placeholder="请输入型号"></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="等级" prop="grade">
              <el-select v-model="vehicleForm.grade" placeholder="请选择等级" style="width: 100%">
                <el-option label="S级" value="S"></el-option>
                <el-option label="A级" value="A"></el-option>
                <el-option label="B级" value="B"></el-option>
                <el-option label="C级" value="C"></el-option>
                <el-option label="D级" value="D"></el-option>
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="品牌Logo" prop="brandLogo">
              <div class="brand-logo-container">
                <!-- 品牌选择 -->
                <div class="brand-select-section">
                  <el-select 
                    v-model="selectedBrand" 
                    placeholder="选择预设品牌" 
                    @change="handleBrandChange"
                    clearable
                    style="width: 100%; margin-bottom: 10px;"
                  >
                    <el-option
                      v-for="brand in brandOptions"
                      :key="brand.value"
                      :label="brand.label"
                      :value="brand.value"
                    >
                      <div class="brand-option">
                        <img v-if="brand.logo" :src="brand.logo" class="brand-logo-small" />
                        <span>{{ brand.label }}</span>
                      </div>
                    </el-option>
                  </el-select>
                  
                  <!-- Logo预览区域 -->
                  <div v-if="vehicleForm.brandLogo" class="logo-preview-area">
                    <div class="preview-label">当前Logo预览：</div>
                    <el-image
                      :src="vehicleForm.brandLogo"
                      fit="contain"
                      style="width: 80px; height: 50px; border: 1px solid #ddd; border-radius: 4px;"
                      :preview-src-list="[vehicleForm.brandLogo]"
                    >
                      <div slot="error" class="image-slot">
                        <i class="el-icon-picture-outline"></i>
                      </div>
                    </el-image>
                  </div>
                </div>
                
                <!-- 自定义上传 -->
                <div class="upload-container">
                  <el-upload
                    class="image-upload"
                    action=""
                    :http-request="handleBrandLogoUpload"
                    :show-file-list="false"
                    :before-upload="beforeImageUpload"
                    accept="image/*"
                  >
                    <div class="upload-area">
                      <div v-if="!vehicleForm.brandLogo" class="upload-placeholder">
                        <i class="el-icon-plus"></i>
                        <div class="upload-text">或上传自定义Logo</div>
                      </div>
                      <img v-else :src="vehicleForm.brandLogo" class="uploaded-image" />
                    </div>
                  </el-upload>
                  <div v-if="vehicleForm.brandLogo" class="image-actions">
                    <el-button size="mini" type="text" @click="previewImage(vehicleForm.brandLogo)">预览</el-button>
                    <el-button size="mini" type="text" @click="removeImage('brandLogo')">删除</el-button>
                  </div>
                </div>
              </div>
            </el-form-item>
          </el-col>
        </el-row>

        <el-form-item label="头部标题" prop="headerTitle">
          <el-input v-model="vehicleForm.headerTitle" placeholder="请输入头部标题"></el-input>
        </el-form-item>

        <!-- ==================== 纯电车型专属字段 ==================== -->
        <template v-if="vehicleForm.energyType === 2">
          <div class="section-title">电池信息</div>
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item label="电芯品牌">
                <el-input v-model="vehicleForm.cellBrand" placeholder="请输入电芯品牌"></el-input>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="电池体系">
                <el-input v-model="vehicleForm.batterySystem" placeholder="请输入电池体系"></el-input>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row :gutter="20">
            <el-col :span="8">
              <el-form-item label="电池能量">
                <el-input v-model="vehicleForm.batteryEnergy" placeholder="请输入">
                  <template slot="append">kWh</template>
                </el-input>
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item label="额定容量">
                <el-input v-model="vehicleForm.ratedCapacity" placeholder="请输入">
                  <template slot="append">Ah</template>
                </el-input>
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item label="额定电压">
                <el-input v-model="vehicleForm.ratedVoltage" placeholder="请输入">
                  <template slot="append">V</template>
                </el-input>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item label="电池健康度">
                <el-input v-model="vehicleForm.batteryHealth" placeholder="0-100">
                  <template slot="append">%</template>
                </el-input>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="预估里程">
                <el-input v-model="vehicleForm.estimatedRange" placeholder="示例300~719">
                  <template slot="append">km</template>
                </el-input>
              </el-form-item>
            </el-col>
          </el-row>

          <!-- 季节续航数据 -->
          <div class="section-sub-title">季节续航数据</div>
          <div v-for="(item, index) in vehicleForm.seasonRangeData" :key="index" class="season-range-row">
            <el-row :gutter="10">
              <el-col :span="6">
                <el-form-item label="季节" :prop="'seasonRangeData.' + index + '.season'" label-width="60px">
                  <el-select v-model="item.season" placeholder="请选择" style="width: 100%">
                    <el-option label="春" value="春"></el-option>
                    <el-option label="夏" value="夏"></el-option>
                    <el-option label="冬" value="冬"></el-option>
                  </el-select>
                </el-form-item>
              </el-col>
              <el-col :span="7">
                <el-form-item label="温度" :prop="'seasonRangeData.' + index + '.temperature'" label-width="60px">
                  <el-input v-model="item.temperature" placeholder="15~25">
                    <template slot="append">°C</template>
                  </el-input>
                </el-form-item>
              </el-col>
              <el-col :span="7">
                <el-form-item label="续航" :prop="'seasonRangeData.' + index + '.range'" label-width="60px">
                  <el-input v-model="item.range" placeholder="300~600">
                    <template slot="append">km</template>
                  </el-input>
                </el-form-item>
              </el-col>
              <el-col :span="4" style="text-align: center; padding-top: 5px;">
                <el-button type="danger" icon="el-icon-delete" circle size="small" @click="removeSeasonRange(index)"></el-button>
              </el-col>
            </el-row>
          </div>
          <el-button type="primary" icon="el-icon-plus" size="small" @click="addSeasonRange" style="margin-bottom: 15px;">添加季节数据</el-button>

          <!-- 电池评分 -->
          <div class="section-sub-title">电池评分</div>
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item label="电池内阻评分">
                <el-input v-model="vehicleForm.internalResistanceScore" placeholder="请输入评分"></el-input>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="电压一致性评分">
                <el-input v-model="vehicleForm.voltageConsistencyScore" placeholder="请输入评分"></el-input>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item label="温度一致性评分">
                <el-input v-model="vehicleForm.temperatureConsistencyScore" placeholder="请输入评分"></el-input>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="充电习惯评分">
                <el-input v-model="vehicleForm.chargingHabitScore" placeholder="请输入评分"></el-input>
              </el-form-item>
            </el-col>
          </el-row>
        </template>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSubmit" :loading="submitLoading">确定</el-button>
      </div>
    </el-dialog>

    <!-- 车辆详情对话框 -->
    <el-dialog title="车辆详情" :visible.sync="detailDialogVisible" width="800px">
      <div v-if="currentVehicle" class="vehicle-detail">
        <el-descriptions :column="2" border>
          <el-descriptions-item label="车辆ID">{{ currentVehicle.vehicleId }}</el-descriptions-item>
          <el-descriptions-item label="品牌">{{ currentVehicle.brand }}</el-descriptions-item>
          <el-descriptions-item label="车型">{{ currentVehicle.model }}</el-descriptions-item>
          <el-descriptions-item label="车架号">{{ currentVehicle.vin }}</el-descriptions-item>
          <el-descriptions-item label="检测日期">{{ currentVehicle.inspectionDate }}</el-descriptions-item>
          <el-descriptions-item label="里程数">{{ currentVehicle.mileage }}公里</el-descriptions-item>
          <el-descriptions-item label="能源类型">{{ currentVehicle.energyType === 2 ? '纯电' : '油车' }}</el-descriptions-item>
          <el-descriptions-item label="排量">{{ currentVehicle.displacement }}</el-descriptions-item>
          <el-descriptions-item label="车辆类型">{{ currentVehicle.vehicleType }}</el-descriptions-item>
          <el-descriptions-item label="型号">{{ currentVehicle.modelNumber }}</el-descriptions-item>
          <el-descriptions-item label="等级">{{ currentVehicle.grade }}</el-descriptions-item>
          <el-descriptions-item label="品牌Logo" :span="2">{{ currentVehicle.brandLogo }}</el-descriptions-item>
          <el-descriptions-item label="头部标题" :span="2">{{ currentVehicle.headerTitle }}</el-descriptions-item>
        </el-descriptions>

        <!-- 纯电车型电池信息 -->
        <template v-if="currentVehicle.energyType === 2">
          <div class="section-title" style="margin-top: 15px;">电池信息</div>
          <el-descriptions :column="2" border>
            <el-descriptions-item label="电芯品牌">{{ currentVehicle.cellBrand || '-' }}</el-descriptions-item>
            <el-descriptions-item label="电池体系">{{ currentVehicle.batterySystem || '-' }}</el-descriptions-item>
            <el-descriptions-item label="电池能量">{{ currentVehicle.batteryEnergy ? currentVehicle.batteryEnergy + ' kWh' : '-' }}</el-descriptions-item>
            <el-descriptions-item label="额定容量">{{ currentVehicle.ratedCapacity ? currentVehicle.ratedCapacity + ' Ah' : '-' }}</el-descriptions-item>
            <el-descriptions-item label="额定电压">{{ currentVehicle.ratedVoltage ? currentVehicle.ratedVoltage + ' V' : '-' }}</el-descriptions-item>
            <el-descriptions-item label="电池健康度">{{ currentVehicle.batteryHealth ? currentVehicle.batteryHealth + '%' : '-' }}</el-descriptions-item>
            <el-descriptions-item label="预估里程">{{ currentVehicle.estimatedRange ? currentVehicle.estimatedRange + ' km' : '-' }}</el-descriptions-item>
            <el-descriptions-item label="季节续航数据" :span="2">{{ getSeasonRangeDisplay(currentVehicle.seasonRangeData) }}</el-descriptions-item>
          </el-descriptions>

          <div class="section-title" style="margin-top: 15px;">电池评分</div>
          <el-descriptions :column="2" border>
            <el-descriptions-item label="电池内阻评分">{{ currentVehicle.internalResistanceScore || '-' }}</el-descriptions-item>
            <el-descriptions-item label="电压一致性评分">{{ currentVehicle.voltageConsistencyScore || '-' }}</el-descriptions-item>
            <el-descriptions-item label="温度一致性评分">{{ currentVehicle.temperatureConsistencyScore || '-' }}</el-descriptions-item>
            <el-descriptions-item label="充电习惯评分">{{ currentVehicle.chargingHabitScore || '-' }}</el-descriptions-item>
          </el-descriptions>
        </template>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { vehicleApi } from '@/api/vehicle'
import { uploadImage } from '@/api/upload'
import { getVehicleBrandOptions } from '@/api/vehicleBrand'

export default {
  name: 'Vehicles',
  data() {
    return {
      loading: false,
      submitLoading: false,
      vehicleList: [],
      searchForm: {
        vehicleId: '',
        brand: '',
        model: ''
      },
      pagination: {
        current: 1,
        size: 10,
        total: 0
      },
      dialogVisible: false,
      detailDialogVisible: false,
      isEdit: false,
      currentVehicle: null,
             vehicleForm: {
         vehicleId: '',
         brand: '',
         model: '',
         vin: '',
         inspectionDate: '',
         mileage: 0,
         displacement: '',
         vehicleType: '',
         modelNumber: '',
         grade: '',
         brandLogo: '',
         headerTitle: '',
         energyType: 1,
         cellBrand: '',
         batterySystem: '',
         batteryEnergy: '',
         ratedCapacity: '',
         ratedVoltage: '',
         batteryHealth: '',
         estimatedRange: '',
         seasonRangeData: [],
         internalResistanceScore: '',
         voltageConsistencyScore: '',
         temperatureConsistencyScore: '',
         chargingHabitScore: ''
       },
       selectedBrand: '', // 选中的品牌
       brandOptions: [], // 品牌选项列表，从车辆品牌管理获取
      vehicleRules: {
        vehicleId: [
          { required: true, message: '请输入车辆ID', trigger: 'blur' }
        ],
        brand: [
          { required: true, message: '请输入品牌', trigger: 'blur' }
        ],
        model: [
          { required: true, message: '请输入车型', trigger: 'blur' }
        ],
        vin: [
          { required: true, message: '请输入车架号', trigger: 'blur' }
        ],
        inspectionDate: [
          { required: true, message: '请选择检测日期', trigger: 'change' }
        ],
        energyType: [
          { required: true, message: '请选择能源类型', trigger: 'change' }
        ]
      }
    }
  },
  computed: {
    dialogTitle() {
      return this.isEdit ? '编辑车辆' : '新增车辆'
    }
  },
  mounted() {
    this.loadData()
    this.loadBrandOptions()
  },
  methods: {
    // 加载品牌选项
    async loadBrandOptions() {
      try {
        const response = await getVehicleBrandOptions()
        if (response && (response.code === 200 || response.code === '200') && response.data) {
          this.brandOptions = response.data.map(brand => ({
            label: brand.brandName,
            value: brand.brandName,
            logo: brand.logoUrl || '',
            id: brand.id
          }))
        }
      } catch (error) {
        console.error('加载品牌选项失败:', error)
        this.$message.error('加载品牌选项失败')
      }
    },

    // 加载数据
    async loadData() {
      this.loading = true
      try {
        const params = {
          current: this.pagination.current,
          size: this.pagination.size,
          ...this.searchForm
        }
        const response = await vehicleApi.getVehicleList(params)
        if (response && (response.code === 200 || response.code === '200') && response.data) {
          this.vehicleList = response.data.records || []
          this.pagination.total = response.data.total || 0
        }
      } catch (error) {
        console.error('加载车辆数据失败:', error)
        this.$message.error('加载数据失败')
      } finally {
        this.loading = false
      }
    },

    // 搜索
    handleSearch() {
      this.pagination.current = 1
      this.loadData()
    },

    // 重置搜索
    resetSearch() {
      this.searchForm = {
        vehicleId: '',
        brand: '',
        model: ''
      }
      this.handleSearch()
    },

    // 分页大小改变
    handleSizeChange(size) {
      this.pagination.size = size
      this.loadData()
    },

    // 当前页改变
    handleCurrentChange(current) {
      this.pagination.current = current
      this.loadData()
    },

    // 显示新增对话框
    showAddDialog() {
      this.isEdit = false
      this.dialogVisible = true
    },

    // 显示编辑对话框
    showEditDialog(row) {
      this.isEdit = true
      this.currentVehicle = row
      this.vehicleForm = { ...row }
      
      // 处理能源类型默认为油车
      if (!this.vehicleForm.energyType) {
        this.vehicleForm.energyType = 1
      }
      
      // 解析季节续航数据 JSON 字符串 → 数组
      if (this.vehicleForm.seasonRangeData && typeof this.vehicleForm.seasonRangeData === 'string') {
        try {
          this.vehicleForm.seasonRangeData = JSON.parse(this.vehicleForm.seasonRangeData)
        } catch (e) {
          this.vehicleForm.seasonRangeData = []
        }
      } else if (!this.vehicleForm.seasonRangeData) {
        this.vehicleForm.seasonRangeData = []
      }
      
      // 检查是否有匹配的预设品牌
      const matchedBrand = this.brandOptions.find(brand => brand.value === row.brand)
      this.selectedBrand = matchedBrand ? matchedBrand.value : ''
      
      this.dialogVisible = true
    },

    // 显示详情对话框
    showDetailDialog(row) {
      this.currentVehicle = row
      this.detailDialogVisible = true
    },

    // 提交表单
    async handleSubmit() {
      this.$refs.vehicleForm.validate(async (valid) => {
        if (valid) {
          this.submitLoading = true
          try {
            // 格式化日期字段
            const submitData = { ...this.vehicleForm }
            
            // 格式化检测日期
            if (submitData.inspectionDate) {
              if (submitData.inspectionDate instanceof Date) {
                submitData.inspectionDate = this.formatDate(submitData.inspectionDate)
              }
            }
            
            // 季节续航数据转为JSON字符串
            if (submitData.seasonRangeData && Array.isArray(submitData.seasonRangeData)) {
              submitData.seasonRangeData = JSON.stringify(submitData.seasonRangeData)
            }
            
            if (this.isEdit) {
              await vehicleApi.updateVehicle(submitData)
              this.$message.success('更新成功')
            } else {
              await vehicleApi.addVehicle(submitData)
              this.$message.success('添加成功')
            }
            this.dialogVisible = false
            this.loadData()
          } catch (error) {
            console.error('提交失败:', error)
            this.$message.error('操作失败')
          } finally {
            this.submitLoading = false
          }
        }
      })
    },

    // 删除车辆
    handleDelete(row) {
      this.$confirm('确定要删除该车辆吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          await vehicleApi.deleteVehicle(row.vehicleId)
          this.$message.success('删除成功')
          this.loadData()
        } catch (error) {
          console.error('删除失败:', error)
          this.$message.error('删除失败')
        }
      }).catch(() => {})
    },

    // 重置表单
    resetForm() {
      this.$refs.vehicleForm.resetFields()
      this.vehicleForm = {
        vehicleId: '',
        brand: '',
        model: '',
        vin: '',
        inspectionDate: '',
        mileage: 0,
        displacement: '',
        vehicleType: '',
        modelNumber: '',
        grade: '',
        brandLogo: '',
        headerTitle: '',
        energyType: 1,
        cellBrand: '',
        batterySystem: '',
        batteryEnergy: '',
        ratedCapacity: '',
        ratedVoltage: '',
        batteryHealth: '',
        estimatedRange: '',
        seasonRangeData: [],
        internalResistanceScore: '',
        voltageConsistencyScore: '',
        temperatureConsistencyScore: '',
        chargingHabitScore: ''
      }
      this.selectedBrand = '' // 重置品牌选择
    },

    // 图片上传前的预处理
    beforeImageUpload(file) {
      const isLt2M = file.size / 1024 / 1024 < 2
      if (!isLt2M) {
        this.$message.error('上传图片大小不能超过 2MB!')
      }
      return isLt2M
    },

    // 添加季节续航数据行
    addSeasonRange() {
      this.vehicleForm.seasonRangeData.push({
        season: '',
        temperature: '',
        range: ''
      })
    },

    // 删除季节续航数据行
    removeSeasonRange(index) {
      this.vehicleForm.seasonRangeData.splice(index, 1)
    },

    // 格式化季节续航数据显示
    getSeasonRangeDisplay(data) {
      if (!data) return '-'
      let list = data
      if (typeof data === 'string') {
        try {
          list = JSON.parse(data)
        } catch (e) {
          return '-'
        }
      }
      if (!Array.isArray(list) || list.length === 0) return '-'
      return list.map(item => `${item.season || '-'} ${item.temperature || '-'} ${item.range || '-'}`).join('；')
    },

    // 处理品牌选择变化
    handleBrandChange(brandName) {
      if (brandName) {
        const selectedBrandOption = this.brandOptions.find(brand => brand.value === brandName)
        if (selectedBrandOption) {
          this.vehicleForm.brandLogo = selectedBrandOption.logo
          this.selectedBrand = brandName
          console.log(`选择品牌: ${brandName}, Logo: ${selectedBrandOption.logo}`)
        }
      } else {
        // 清空选择
        this.vehicleForm.brandLogo = ''
        this.selectedBrand = ''
      }
    },

    // 处理品牌Logo上传
    async handleBrandLogoUpload(options) {
      try {
        const response = await uploadImage(options.file)
        if (response.code === '200') {
          this.vehicleForm.brandLogo = response.data
          this.selectedBrand = '' // 清空品牌选择
          this.$message.success('Logo上传成功')
        } else {
          this.$message.error(response.msg || 'Logo上传失败')
        }
      } catch (error) {
        console.error('Logo上传失败:', error)
        this.$message.error('Logo上传失败')
      }
    },



    // 预览图片
    previewImage(url) {
      window.open(url, '_blank')
    },

    // 移除图片
    removeImage(field) {
      this.vehicleForm[field] = ''
      this.$message.success('图片已移除')
    },
    
    // 格式化日期为 yyyy-MM-dd 格式
    formatDate(date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }
  }
}
</script>

<style scoped>
.vehicles-container {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0;
  color: #303133;
}

.search-section {
  background: #fff;
  padding: 20px;
  border-radius: 4px;
  margin-bottom: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.search-form {
  margin: 0;
}

.table-section {
  background: #fff;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.pagination-section {
  padding: 20px;
  text-align: right;
}

/* 操作按钮样式优化 */
.el-button--mini {
  margin-right: 5px;
}

.el-button--mini:last-child {
  margin-right: 0;
}

/* 表格内容垂直居中 */
.el-table .cell {
  display: flex;
  align-items: center;
  justify-content: center;
}

.el-table .cell:first-child {
  justify-content: flex-start;
}

/* 操作列按钮容器 */
.el-table .el-button + .el-button {
  margin-left: 5px;
}

.vehicle-detail {
  padding: 20px 0;
}

.dialog-footer {
  text-align: right;
}

/* 图片上传样式 */
.upload-container {
  position: relative;
  width: 100%;
  height: 100px; /* 固定高度 */
  border: 1px dashed #d9d9d9;
  border-radius: 4px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f5f7fa;
}

.upload-area {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #909399;
  font-size: 14px;
}

.upload-placeholder i {
  font-size: 28px;
  margin-bottom: 5px;
}

.upload-text {
  font-size: 12px;
}

.uploaded-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-actions {
  position: absolute;
  top: 0;
  right: 0;
  background-color: rgba(0, 0, 0, 0.5);
  border-radius: 0 4px 0 4px;
  padding: 5px;
  display: flex;
  gap: 5px;
  z-index: 10;
}

.image-actions .el-button {
  color: #fff;
  font-size: 12px;
}

/* 品牌Logo选择样式 */
.brand-logo-container {
  width: 100%;
}

.brand-select-section {
  margin-bottom: 10px;
}

.logo-preview-area {
  margin-top: 10px;
  padding: 10px;
  background-color: #f9f9f9;
  border-radius: 4px;
  border: 1px solid #e6e6e6;
}

.preview-label {
  font-size: 12px;
  color: #666;
  margin-bottom: 8px;
}

.brand-option {
  display: flex;
  align-items: center;
  gap: 8px;
}

.brand-logo-small {
  width: 20px;
  height: 20px;
  object-fit: contain;
  border-radius: 2px;
}

.brand-cell {
  display: flex;
  align-items: center;
}

.image-slot-small {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  background: #f5f7fa;
  color: #909399;
}

.image-slot-small i {
  font-size: 12px;
}

/* 能源及电池信息分区标题 */
.section-title {
  font-size: 15px;
  font-weight: bold;
  color: #303133;
  margin-bottom: 15px;
  padding-left: 10px;
  border-left: 3px solid #409eff;
  line-height: 1.2;
}

.section-sub-title {
  font-size: 13px;
  font-weight: bold;
  color: #606266;
  margin-bottom: 10px;
  padding-left: 8px;
  border-left: 2px solid #c0c4cc;
  line-height: 1.2;
}

.season-range-row {
  margin-bottom: 5px;
}

.season-range-row .el-form-item {
  margin-bottom: 10px;
}
</style>